import { useSyncExternalStore } from "react";
import { getLogger } from "@/utils/logger";

const log = getLogger("networkStatus");

const DEFAULT_RATE_LIMIT_MS = 30 * 1000;
const RATE_LIMIT_TICK_INTERVAL_MS = 1000;

const listeners = new Set();
let rateLimitTickerId = null;

let state = {
  isOffline: typeof navigator !== "undefined" ? !navigator.onLine : false,
  rateLimitUntil: null,
  serverError: null,
  retryRequest: null,
  isRetrying: false,
};

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const setState = (nextState) => {
  state = typeof nextState === "function" ? nextState(state) : { ...state, ...nextState };
  emitChange();
};

const clearRateLimitTicker = () => {
  if (!rateLimitTickerId) return;
  clearInterval(rateLimitTickerId);
  rateLimitTickerId = null;
};

const startRateLimitTicker = () => {
  clearRateLimitTicker();
  if (!state.rateLimitUntil) return;

  rateLimitTickerId = setInterval(() => {
    if (!state.rateLimitUntil || Date.now() >= state.rateLimitUntil) {
      clearRateLimitTicker();
      emitChange();
      return;
    }
    emitChange();
  }, RATE_LIMIT_TICK_INTERVAL_MS);
};

const getRateLimitSecondsRemaining = () => {
  if (!state.rateLimitUntil) return 0;
  return Math.max(0, Math.ceil((state.rateLimitUntil - Date.now()) / 1000));
};

const parseRetryAfterSeconds = (retryAfterHeader) => {
  const parsed = Number.parseInt(retryAfterHeader, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

export const setOffline = () => {
  if (state.isOffline) return;
  log.warn("[networkStatus:setOffline] > [Response]: browser is offline");
  setState((previousState) => ({ ...previousState, isOffline: true }));
};

export const setOnline = () => {
  if (!state.isOffline) return;
  log.info("[networkStatus:setOnline] > [Response]: browser is online");
  setState((previousState) => ({ ...previousState, isOffline: false }));
};

export const reportRateLimited = ({ retryAfterHeader, retryRequest, url }) => {
  const retryAfterSeconds = parseRetryAfterSeconds(retryAfterHeader);
  const retryAfterMs =
    retryAfterSeconds !== null ? retryAfterSeconds * 1000 : DEFAULT_RATE_LIMIT_MS;
  const retryUntil = Date.now() + retryAfterMs;

  log.warn("[networkStatus:reportRateLimited] > [Response]: 429 received", {
    retryAfterMs,
    url,
  });

  setState((previousState) => ({
    ...previousState,
    rateLimitUntil: retryUntil,
    serverError: null,
    retryRequest: retryRequest || previousState.retryRequest,
  }));
  startRateLimitTicker();
};

export const reportServerError = ({ status, url, retryRequest }) => {
  log.error("[networkStatus:reportServerError] > [Error]: 5xx received", {
    status,
    url,
  });

  setState((previousState) => ({
    ...previousState,
    serverError: {
      status,
      message: "Something went wrong on the server. Please try again.",
    },
    retryRequest: retryRequest || previousState.retryRequest,
  }));
};

export const clearApiNetworkErrors = () => {
  if (!state.rateLimitUntil && !state.serverError && !state.retryRequest) return;
  clearRateLimitTicker();
  setState((previousState) => ({
    ...previousState,
    rateLimitUntil: null,
    serverError: null,
    retryRequest: null,
  }));
};

export const clearServerNetworkError = () => {
  if (!state.serverError) return;
  setState((previousState) => ({
    ...previousState,
    serverError: null,
  }));
};

export const retryLastFailedRequest = async () => {
  if (!state.retryRequest || state.isRetrying) return false;
  if (getRateLimitSecondsRemaining() > 0) return false;

  log.info("[networkStatus:retryLastFailedRequest] > [Request]: retry failed request");
  setState((previousState) => ({ ...previousState, isRetrying: true }));

  try {
    await state.retryRequest();
    log.info("[networkStatus:retryLastFailedRequest] > [Response]: retry succeeded");
    clearApiNetworkErrors();
    return true;
  } catch (error) {
    log.error("[networkStatus:retryLastFailedRequest] > [Error]: retry failed", error);
    return false;
  } finally {
    setState((previousState) => ({ ...previousState, isRetrying: false }));
  }
};

export const initializeNetworkStatusListeners = () => {
  if (typeof window === "undefined") return () => {};

  const handleOnline = () => setOnline();
  const handleOffline = () => setOffline();

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  if (!navigator.onLine) {
    setOffline();
  }

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    clearRateLimitTicker();
  };
};

export const subscribeNetworkStatus = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getNetworkStatusSnapshot = () => state;

export const useNetworkStatus = () => {
  const snapshot = useSyncExternalStore(subscribeNetworkStatus, getNetworkStatusSnapshot);
  return {
    ...snapshot,
    isRateLimited: snapshot.rateLimitUntil !== null,
    rateLimitSecondsRemaining: getRateLimitSecondsRemaining(),
  };
};
